import React, { useEffect, useState } from 'react';
import NavBar from './components/Navbar';
import SearchBar from './components/SearchBar';
import AdvancedSearch from './components/AdvancedSearch';
import SearchOptions from './components/SearchOptions';
import EntityAlphaSearch from './components/EntityAlphaSearch';
import { fetchAuthorsByAlpha, fetchPublishersByAlpha, fetchDepartmentsByAlpha, fetchCategoriesByAlpha } from './api/entityFetchers';
import ProductTable from './components/ProductTable';
import ProductGrid from './components/ProductGrid';
import ProductModal from './components/ProductModal';
import RackManagementModal from './components/RackManagementModal';
import Pagination from './components/Pagination';
import BranchSelection from './components/BranchSelection';
import { Container, Row, Col } from 'react-bootstrap';
import Loader from './components/Loader';
import './assets/scss/custom-bootstrap.scss';
import { Layout } from 'antd';

const App = () => {
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
    const [view, setView] = useState('grid');
    const [productType, setProductType] = useState('books');
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [modalShow, setModalShow] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchParams, setSearchParams] = useState({});
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [showBranchSelect, setShowBranchSelect] = useState(false);
    const [loading, setLoading] = useState(false);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc');
    const [rackOptions, setRackOptions] = useState([]);
    // New state for entity search
    const [entitySearchType, setEntitySearchType] = useState(null); // 'author', 'publisher', 'department', 'category'
    const [entitySelected, setEntitySelected] = useState(null);
    const [showRackModal, setShowRackModal] = useState(false);

    useEffect(() => {
        const storedBranch = localStorage.getItem('selectedBranch');
        if (storedBranch) {
            setSelectedBranch(JSON.parse(storedBranch));
        } else {
            setShowBranchSelect(true);
        }
    }, []);

    useEffect(() => {
        const fetchRacks = async () => {
            try {
                const token = localStorage.getItem('userToken');
                const response = await fetch(`https://live.jeyaagency.com/api/racks?token=${token}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );
                const data = await response.json();
                if (data.status_code === "0" && Array.isArray(data.object)) {
                    setRackOptions(data.object);
                }
            } catch (error) {
                console.error('Error fetching rack options:', error);
            }
        };
        fetchRacks();
    }, []);

    useEffect(() => {
        if (Object.keys(searchParams).length > 0) {
            handleSearch(searchParams, currentPage);
        }
    }, [currentPage, searchParams, productType,sortField,sortOrder]);

    const handleSearch = async (params, page = 1) => {
        setLoading(true);
        const queryString = new URLSearchParams(params).toString();
        let updatedParams = queryString + "&is_book=" + (productType === 'books' ? '1' : '2');
        
        // Add sorting parameters if sorting is active
        if (sortField) {
            updatedParams += `&sortBy=${sortField}&order=${sortOrder}`;
        }
        
        const response = await fetch(`https://live.jeyaagency.com/api/global/search?${updatedParams}&page=${page}`);
        const data = await response.json();

        setProducts(data.items.data || []);
        setCurrentPage(data.items.current_page);
        setTotalPages(data.items.last_page);
        setLoading(false);
    };

    const handleShowDetails = (product) => {
        const getStock = async () => {
            const response = await fetch(`https://live.jeyaagency.com/api/global/search/stock?id=${product.item_id}&branch_id=${selectedBranch.id}`);
            const data = await response.json();
            try {
                product.branches = data.branch_stock;
                product.branch_info = data.branch_info;
                setSelectedProduct(product);
                setModalShow(true);
                //setShowRackModal(true);
            } catch(error) {
                console.log(error);
            }
        };
        getStock();
    };

    const handleEditRack = (product) => {
        setSelectedProduct(product);
        setShowRackModal(true);
    };

    const handleSearchSubmit = (params) => {
        console.log(productType,params);
        setSearchParams(params);
        setCurrentPage(1); 
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleBranchChange = (branch) => {
        const branchData = {
            id: branch.id,
            branch: branch.branch
        };
        setSelectedBranch(branchData);
        localStorage.setItem('selectedBranch', JSON.stringify(branchData));
        
        // If user is logged in, update their branch as well
        if (localStorage.getItem('userToken')) {
            localStorage.setItem('userBranch', JSON.stringify(branchData));
        }
        
        // Refresh product data with new branch
        if (Object.keys(searchParams).length > 0) {
            handleSearch(searchParams, currentPage);
        }
    };

    const handleSort = (field, order) => {
        setSortField(field);
        setSortOrder(order);
    };

    return (
        <div>
            {!selectedBranch && (
                <BranchSelection 
                    show={showBranchSelect}
                    onSelectBranch={handleBranchChange}
                />
            )}
            {selectedBranch && (
                <>
                    <NavBar 
                        selectedBranch={selectedBranch}
                        onSearchToggle={() => setShowAdvancedSearch(false)}
                        onAdvancedSearchToggle={() => setShowAdvancedSearch(true)}
                        onBranchChange={handleBranchChange}
                    />
                    <Container className="mt-4">
                        <Row>
                            <Col className="text-center">
                                {showAdvancedSearch ? (
                                    <AdvancedSearch onSearch={handleSearchSubmit} />
                                ) : (
                                    <SearchBar onSearch={handleSearchSubmit} />
                                )}
                            </Col>
                        </Row>
                        {/* Books/Stationery Toggle Section - now above search options */}
                        <Row className="mb-3">
                            <Col className="d-flex justify-content-end align-items-center">
                                <div className="btn-group me-2" role="group" aria-label="Product Type">
                                    <button
                                        type="button"
                                        className={`btn btn-outline-primary ${productType === 'books' ? 'active' : ''}`}
                                        onClick={() => {
                                            setProductType('books');
                                        }}
                                    >
                                        Books
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn btn-outline-primary ${productType === 'stationery' ? 'active' : ''}`}
                                        onClick={() => {
                                            setProductType('stationery');
                                        }}
                                    >
                                        Stationery
                                    </button>
                                </div>
                                <div className="btn-group" role="group" aria-label="View Options">
                                    <button
                                        type="button"
                                        className={`btn btn-outline-secondary ${view === 'table' ? 'active' : ''}`}
                                        onClick={() => setView('table')}
                                    >
                                        <i className="bi bi-table"></i>
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn btn-outline-secondary ${view === 'grid' ? 'active' : ''}`}
                                        onClick={() => setView('grid')}
                                    >
                                        <i className="bi bi-grid"></i>
                                    </button>
                                </div>
                            </Col>
                        </Row>
                        {/* New Search Options Section - now below Books/Stationery toggle */}
                        <Row className="mb-3">
                            <Col>
                                {!entitySearchType && (
                                    <SearchOptions onSelect={(type) => {
                                        setEntitySearchType(type);
                                        setEntitySelected(null);
                                        setCurrentPage(1);
                                        setProducts([]);
                                        setSearchParams({});
                                        setShowAdvancedSearch(false);
                                        if (window.advancedSearchRef && window.advancedSearchRef.resetFields) {
                                            window.advancedSearchRef.resetFields();
                                        }
                                    }} />
                                )}
                                {entitySearchType && (
                                    <div>
                                        <div className="mb-2" style={{ fontWeight: 600, fontSize: 16, color: '#4a4a4a', textAlign: 'center' }}>
                                            {(() => {
                                                switch(entitySearchType) {
                                                    case 'author': return 'Author';
                                                    case 'publisher': return 'Publisher';
                                                    case 'department': return 'Department';
                                                    case 'category': return 'Category';
                                                    default: return '';
                                                }
                                            })()} Alphabet Search
                                        </div>
                                        <div className="d-flex justify-content-end mt-2">
                                            <button className="btn btn-link text-danger" onClick={() => { setEntitySearchType(null); setEntitySelected(null); setProducts([]); }}>Back to Search Options</button>
                                        </div>
                                        <EntityAlphaSearch
                                            entityType={entitySearchType}
                                            fetchOptions={alpha => {
                                                switch(entitySearchType) {
                                                    case 'author': return fetchAuthorsByAlpha(alpha);
                                                    case 'publisher': return fetchPublishersByAlpha(alpha);
                                                    case 'department': return fetchDepartmentsByAlpha(alpha);
                                                    case 'category': return fetchCategoriesByAlpha(alpha);
                                                    default: return Promise.resolve([]);
                                                }
                                            }}
                                            onSelect={item => {
                                                // Update AdvancedSearch fields and switch to advanced search view
                                                let advancedSearchFields = {};
                                                switch(entitySearchType) {
                                                    case 'author': advancedSearchFields = { author: item.name }; break;
                                                    case 'publisher': advancedSearchFields = { publisher: item.name }; break;
                                                    case 'department': advancedSearchFields = { department: item.id }; break;
                                                    case 'category': advancedSearchFields = { category: item.id }; break;
                                                    default: advancedSearchFields = {}; break;
                                                }
                                                setEntitySearchType(null); // Close entity search UI
                                                setEntitySelected(null);
                                                setProducts([]); // Clear previous product results
                                                setCurrentPage(1);
                                                setShowAdvancedSearch(true);
                                                setSearchParams(advancedSearchFields);
                                                setTimeout(() => {
                                                    if (window.advancedSearchRef && window.advancedSearchRef.setFieldsValue) {
                                                        window.advancedSearchRef.setFieldsValue(advancedSearchFields);
                                                    }
                                                }, 0);
                                            }}
                                        />
                                        
                                    </div>
                                )}
                            </Col>
                        </Row>
                        {/* Hide product list if entity search is active */}
                        {!entitySearchType && (
                        <Row>
                            <Col>
                                {loading ? (
                                    <Loader />
                                ) : (
                                    products.length > 0 ? (
                                        view === 'table' ? (
                                            <ProductTable 
                                                products={products} 
                                                onShowDetails={handleShowDetails}
                                                onEditRack={handleEditRack}
                                                onSort={handleSort}
                                                sortField={sortField}
                                                sortOrder={sortOrder}
                                            />
                                        ) : (
                                            <ProductGrid onEditRack={handleEditRack} products={products} onShowDetails={handleShowDetails} />
                                        )
                                    ) : (
                                        <div className="alert alert-danger" role="alert">
                                            No products available to list, please enter keywords and search
                                        </div>
                                    )
                                )}
                            </Col>
                        </Row>
                        )}
                        {products.length > 0 && !entitySearchType && (
                            <Row>
                                <Col className="d-flex justify-content-center">
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                    />
                                </Col>
                            </Row>
                        )}
                        <Layout.Footer style={{ textAlign: 'center', fontSize: 12, color: '#888', background: '#fafafa', borderTop: '1px solid #f0f0f0', padding: '10px 0' }}>
                Developed by Revonetics Pvt Ltd
            </Layout.Footer>
                    </Container>
                    {selectedProduct && (
                        <>
                            <ProductModal
                                show={modalShow}
                                onHide={() => setModalShow(false)}
                                product={selectedProduct}
                                rackOptions={rackOptions}
                            />
                            <RackManagementModal
                                visible={showRackModal}
                                onClose={() => setShowRackModal(false)}
                                product={selectedProduct}
                                branchId={selectedBranch?.id}
                                rackOptions={rackOptions}
                                onRackUpdate={() => {
                                    // Refresh product data after rack update if needed
                                    if (Object.keys(searchParams).length > 0) {
                                        handleSearch(searchParams, currentPage);
                                    }
                                }}
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default App;